import { PosReportViewModel } from './../models/pos-report-view.model';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, pipe } from 'rxjs';
import { InvoiceReportModel } from '../models/invoice-report.model';
import { catchError, timeout } from 'rxjs/operators';
import { CurrentUsageTenantSummaryModel, ChartModel, TenantSubscriptionSummary, SubscriptionUsageLineDetail, TenantLicenseSummary, TenantDailyResourceUsage, CurrentUsageRequestModel } from '../models/azure-dashboard-view.model';
import { AuthService } from "./auth.service";
import { Order } from '../models/orders.model';
import { ApiResponse } from '../models/api-response.model';

@Injectable()
export class ReportingService {

  base_url: string = "api/Reporting";

  constructor(
    private http: HttpClient,
    private authService: AuthService

  ) { }

  // #region reporting lists

  customReportFilters: Array<string> = [
    '',
    'REPORTING PERIOD',
    'BRAND',
    'CUSTOMER REFERENCE',
    'STATE',
  ];

  filterTimePeriods: Array<string> = [
    '30 DAYS',
    '60 DAYS',
    'QUARTER TO DATE',
    'LAST MONTH',
    'ALL OPEN QUOTES',
    'SPECIFIC DATES',
  ];

  states: Array<string> = [
    'NSW',
    'QLD',
    'VIC',
    'NT',
    'WA',
    'SA',
    'TAS'
  ];

  statesNZ: Array<string> = [];

  schedulingFreq: Array<string> = [
    'DOWNLOAD NOW'
    , 'ONCE'
    , 'WEEKLY'
    , 'MONTHLY'
  ]

  // #endregion

  // #region API CALLS

  getInvoiceReportSummary(startDate: string, endDate: string): Observable<InvoiceReportModel[]> {
    return this.http.get<InvoiceReportModel[]>(this.base_url + '/GetInvoiceReportSummary?startDate=' + startDate + '&endDate=' + endDate)
      .pipe(catchError(this.authService.handleError));
  }

  getAzureInvoiceReportSummary(startDate: string, endDate: string): Observable<InvoiceReportModel[]> {
    return this.http.get<InvoiceReportModel[]>(this.base_url + '/GetAzureInvoiceReportSummary?startDate=' + startDate + '&endDate=' + endDate)
      .pipe(catchError(this.authService.handleError));
  }

  getAzureCurrentUsageTenantSummary(isLastMonth: boolean): Observable<CurrentUsageTenantSummaryModel> {
    return this.http.get<CurrentUsageTenantSummaryModel>(this.base_url + '/GetAzureCurrentUsageTenantSummary?isLastMonth=' + isLastMonth)
      .pipe(catchError(this.authService.handleError));
  }

  getAzureEstimatedTenantSummary(startDate: string, endDate: string): Observable<CurrentUsageTenantSummaryModel> {
    return this.http.get<CurrentUsageTenantSummaryModel>(this.base_url + '/GetAzureEstimatedTenantSummary?startDate=' + startDate + '&endDate=' + endDate)
      .pipe(catchError(this.authService.handleError));
  }
  getAzureEstimatedTenantSummaryArchive(startDate: string, endDate: string): Observable<CurrentUsageTenantSummaryModel> {
    return this.http.get<CurrentUsageTenantSummaryModel>(this.base_url + '/GetAzureEstimatedTenantSummary?startDate=' + startDate + '&endDate=' + endDate)
      .pipe(catchError(this.authService.handleError));
  }

  getAzureCurrentUsageResourceSummary(): Observable<ChartModel[]> {
    return this.http.get<ChartModel[]>(this.base_url + '/GetAzureCurrentUsageResourceSummary')
      .pipe(catchError(this.authService.handleError));
  }

  getAzureUsageSummary(startDate: string, endDate: string): Observable<TenantSubscriptionSummary[]> {
    return this.http.get<TenantSubscriptionSummary[]>(this.base_url + '/GetAzureUsageSummary?startDate=' + startDate + '&endDate=' + endDate)
      .pipe(catchError(this.authService.handleError));
  }
  getAzureUsageSummaryArchive(startDate: string, endDate: string): Observable<TenantSubscriptionSummary[]> {
    return this.http.get<TenantSubscriptionSummary[]>(this.base_url + '/GetAzureUsageSummary_Archive?startDate=' + startDate + '&endDate=' + endDate)
      .pipe(catchError(this.authService.handleError));
  }

  getAzureTopUsageTenants(startDate: string, endDate: string): Observable<ChartModel[]> {
    return this.http.get<ChartModel[]>(this.base_url + '/GetAzureTopUsageTenants?startDate=' + startDate + '&endDate=' + endDate)
      .pipe(
        timeout(300000),
        catchError(this.authService.handleError));
  }

  getAzureTopUsageResources(startDate: string, endDate: string): Observable<ChartModel[]> {
    return this.http.get<ChartModel[]>(this.base_url + '/GetAzureTopUsageResources?startDate=' + startDate + '&endDate=' + endDate)
      .pipe(catchError(this.authService.handleError));
  }

  getTenantHistoricalDailyUsage(tenantId: string, subscriptionId: string, startDate: string, endDate: string): Observable<SubscriptionUsageLineDetail[]> {
    return this.http.get<SubscriptionUsageLineDetail[]>(this.base_url + '/GetTenantHistoricalDailyUsage?tenantId=' + tenantId + '&subscriptionId=' + subscriptionId + '&startDate=' + startDate + '&endDate=' + endDate)
      .pipe(catchError(this.authService.handleError));
  }

  getLicenseReportSummary(): Observable<TenantLicenseSummary[]> {
    return this.http.get<TenantLicenseSummary[]>(this.base_url + '/GetLicenseReportSummary')
      .pipe(catchError(this.authService.handleError));
  }

  getPosReport(startDate: string, endDate: string): Observable<PosReportViewModel> {
    return this.http.get<PosReportViewModel>(this.base_url + "/GetPosReport?startDate=" + startDate + "&endDate=" + endDate)
      .pipe(catchError(this.authService.handleError));
  }

  getDailyTenantUsage(model: CurrentUsageRequestModel): Observable<ChartModel[]> {
    return this.http.post<ChartModel[]>(this.base_url + "/GetDailyTenantUsage", model)
      .pipe(catchError(this.authService.handleError));
  }

  getDailyTenantUsageCategory(model: CurrentUsageRequestModel): Observable<ChartModel[]> {
    return this.http.post<ChartModel[]>(this.base_url + "/GetDailyTenantUsageCategory", model)
      .pipe(catchError(this.authService.handleError));
  }

  getTenantCurrentResourceList(model: CurrentUsageRequestModel): Observable<TenantDailyResourceUsage[]> {
    return this.http.post<TenantDailyResourceUsage[]>(this.base_url + "/GetTenantCurrentResourceList", model)
      .pipe(catchError(this.authService.handleError));
  }

  getBackOrders(): Observable<Order[]> {
    return this.http.get<Order[]>(this.base_url + "/GetBackOrders")
      .pipe(catchError(this.authService.handleError));
  }

  exportCSPInvoice(startDate: string, endDate: string): Observable<any> {
    return this.http.get("/api/ExportExcel/exportcspinvoice?startDate=" + startDate + "&endDate=" + endDate,
      { responseType: 'blob', headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' } })
      .pipe(catchError(this.authService.handleError));
  }

  exportCSPUsageInvoice(startDate: string, endDate: string, invoiceNo?: string, subscriptionId?: string): Observable<any> {
    return this.http.get("/api/ExportExcel/exportcspusageinvoice?startDate=" + startDate + "&endDate=" + endDate + "&invoiceNo=" + invoiceNo + "&subscriptionId=" + subscriptionId,
      { responseType: 'blob', headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' } })
      .pipe(catchError(this.authService.handleError));
  }

  exportHistoricalUsageReport(startDate: string, endDate: string, subId?: string): Observable<any> {
    return this.http.get("/api/ExportExcel/exportazureusagehistoricalsummary?startDate=" + startDate + "&endDate=" + endDate + "&subscriptionId=" + subId,
      { responseType: 'blob', headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' } })
      .pipe(catchError(this.authService.handleError));
  }  
  exportHistoricalUsageReportArchive(startDate: string, endDate: string, subId?: string): Observable<any> {
    return this.http.get("/api/ExportExcel/exportazureusagehistoricalsummary_archive?startDate=" + startDate + "&endDate=" + endDate + "&subscriptionId=" + subId,
      { responseType: 'blob', headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' } })
      .pipe(catchError(this.authService.handleError));
  }  
  exportCurrent_Unbilled_UsageReport(tenantId: string, subscriptionId: string, isLastMonth?: boolean): Observable<any> {
    return this.http.get("/api/ExportExcel/Export_Unbilled_UsageReport?tenantId=" + tenantId + "&subscriptionId=" + subscriptionId + "&isLastMonth=" + isLastMonth,
      { responseType: 'blob', headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' } })
      .pipe(catchError(this.authService.handleError));
  }  

  export_Estimated_UsageReport(startDate: string, endDate: string, tenantId: string, subscriptionId: string): Observable<any> {
    return this.http.get("/api/ExportExcel/Export_Estimated_UsageReport?startDate=" + startDate + "&endDate=" + endDate + "&tenantId=" + tenantId + "&subscriptionId=" + subscriptionId,
      { responseType: 'blob', headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' } })
      .pipe(catchError(this.authService.handleError));
  }  

  exportLicenseDeploymentReportExcel(): Observable<any> {
    return this.http.get("/api/ExportExcel/exportlicensedeploymentreport",
      { responseType: 'blob', headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' } })
      .pipe(catchError(this.authService.handleError));
  }

  copyBackOrdersReportLink(): Observable<any> {
    return this.http.get("/api/ExportExcel/GetBackOrdersLink")
      .pipe(catchError(this.authService.handleError));
  }
  copySellThroughReportLink(): Observable<any> {
    return this.http.get("/api/ExportExcel/GetSellThroughLink")
      .pipe(catchError(this.authService.handleError));
  }

  exportBackOrdersReport(): Observable<any> {
    return this.http.get("/api/ExportExcel/ExportBackOrdersReport",
      { responseType: 'blob', headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' } })
      .pipe(catchError(this.authService.handleError));
  }

  exportPosReport(startDate: string, endDate: string): Observable<any> {
    return this.http.get("/api/ExportExcel/debtorstockmovementdownload?startDate=" + startDate + "&endDate=" + endDate,
      { responseType: 'blob', headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' } })
      .pipe(catchError(this.authService.handleError));

  }

  getEsdReport(startDate, endDate): Observable<any> {
    return this.http.get(this.base_url + "/GetEsdReport?startDate=" + startDate + "&endDate=" + endDate)
      .pipe(catchError(this.authService.handleError));
  }

  getWebAuditLog(startDate, endDate): Observable<any> {
    return this.http.get(this.base_url + "/GetWebAuditLog?startDate=" + startDate + "&endDate=" + endDate)
      .pipe(catchError(this.authService.handleError));
  }

  getSalesLicenseKey(orderNo, suffix): Observable<any> {
    return this.http.get(this.base_url + "/GetSalesLicenseKey?orderNo=" + orderNo + "&suffix=" + suffix)
      .pipe(catchError(this.authService.handleError));
  }

  exportEsdReport(startDate: string, endDate: string): Observable<any> {
    return this.http.get("/api/ExportExcel/exportEsdReport?startDate=" + startDate + "&endDate=" + endDate,
      { responseType: 'blob', headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' } })
      .pipe(catchError(this.authService.handleError));
  }

  exportWebAuditLog(startDate: string, endDate: string): Observable<any> {
    return this.http.get("/api/ExportExcel/ExportWebAuditLog?startDate=" + startDate + "&endDate=" + endDate,
    { responseType: 'blob', headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' } })
    .pipe(catchError(this.authService.handleError));
  }

  exportEsdLicencsewithStatus(orderNo: string, suffix: string): Observable<any> {
    return this.http.get("/api/ExportExcel/exportEsdLicensewithStatus?orderNo=" + orderNo + "&suffix=" + suffix,
      { responseType: 'blob', headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' } })
      .pipe(catchError(this.authService.handleError));
  }

  getUpcomingMicrosoftCSPRenewals(startDate: string, endDate: string): Observable<any> {
    return this.http.get<any>(this.base_url + '/GetMicrosoftCSPRenewals?startDate=' + startDate + '&endDate=' + endDate)
      .pipe(catchError(this.authService.handleError));
  }

  exportMSCSPRenewals(startDate: string, endDate: string): Observable<any> {
    return this.http.get('/api/ExportExcel/exportMsCspRenewals?startDate=' + startDate + '&endDate=' + endDate,
      { responseType: 'blob', headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' } })
      .pipe(catchError(this.authService.handleError));
  }
  getNCEOrdersReport(): Observable<any> {
    return this.http.get<any>(this.base_url + "/GetNCEOrdersReport")
      .pipe(catchError(this.authService.handleError));
  }  
  GetNCEOrdersReportData(): Observable<any> {
    return this.http.get("/api/ExportExcel/ExportNCEOrders",
      { responseType: 'blob', headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' } })
      .pipe(catchError(this.authService.handleError));
  }
  copyNCEOrdersReportLink(): Observable<any> {
    return this.http.get("/api/ExportExcel/GetExportNCEOrdersLink")
      .pipe(catchError(this.authService.handleError));
  } 
  getNewsletterReport(catalogId: number, startDate:string , endDate:string): Observable<any> {
    return this.http.get(this.base_url + "/GetNewsletterReport?newsletterId=" + catalogId +"&startDate=" + startDate + "&endDate=" + endDate)
      .pipe(catchError(this.authService.handleError));
  }

  exportNewsletterReport(catalogId:number, startDate: string, endDate: string): Observable<any> {
    return this.http.get("/api/ExportExcel/DownloadNewsletterReport?newsletterId=" + catalogId +"&startDate=" + startDate + "&endDate=" + endDate,
      { responseType: 'blob', headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' } })
      .pipe(catchError(this.authService.handleError));
  }

  exportAnalyticsReport(analyticsTypeId: number, startDate: string, endDate: string, ): Observable<any> {
    return this.http.get("/api/ExportExcel/DownloadAnalyticsReport?analyticsTypeId=" + analyticsTypeId +"&startDate=" + startDate + "&endDate=" + endDate + "&analyticsTypeId=" + analyticsTypeId,
      { responseType: 'blob', headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' } })
      .pipe(catchError(this.authService.handleError));
  }
}

